import { useMemo, memo } from 'react';
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Tab from "../../../components/Tab";
import Logo from "../../../components/Logo";
import Navigation from "../../../components/NavigationBar";
import CrewUserStatus from "../../../features/crew-users/component/CrewUserStatus";
import { get } from "lodash";
import { Divider } from "@mui/material";
import assetHashChecker from "../../../utils/assetHashChecker";
import { apiUrl } from '../../../utils/apiUrl';

const clearLastSavedState = () => {
  const keysToKeep = [
    "token-type",
    "clientId",
    "uid",
    "expiry",
    "client",
    "access-token",
  ];

  Object.keys(localStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  document.location.href = "/";
};

const clearServiceWorkerCache = () => {
  assetHashChecker.manualClear();
};

const isMobileMode = window.innerWidth <= 600;

const NavigationBar = ({
  currentTab,
  tabs,
  push,
  username,
  isLoading = false,
  onLogout,
  avatar,
  status,
  id,
  onStatusChange,
  permissions,
}) => {
  // R19 + RR7 surface the spread-key warning loudly: the legacy
  // `<Tab {...tab}/>` form spreads `tab.key` through, which
  // duplicates the JSX `key`. Pull `key` out of the spread.
  //
  // `to={'/' + value}` opts each navbar tab into the
  // RouterLink-backed rendering path inside `<CustomTab>` (see
  // the navbar same-section-click fix and its sub-tab follow-up
  // in src/components/Tab/index.jsx). Sub-tabs that share the
  // same component leave `to` undefined.
  const tabItems = useMemo(
    () => tabs.map(({ key, value, ...rest }) => (
      <Tab key={key} value={value} to={`/${value}`} {...rest} />
    )),
    [tabs],
  );

  // Avatar URL: `apiUrl` already returns undefined for an empty value,
  // so the guard is belt-and-braces — before it existed the raw template
  // fired `apinull` / `apiundefined` 404s in dev (cosmetic but loud).
  const avatarSrc = avatar ? apiUrl(avatar) : undefined;

  return (
    <Navigation
      isLoading={isLoading}
      logo={<Logo />}
      tabs={tabItems}
      navItems={tabs}
      username={isMobileMode ? "" : username}
      currentTab={currentTab}
      onTabChange={push}
      avatar={avatarSrc}
      menuItems={[
        // Keyed Fragment: a bare `<>...</>` inside an array doesn't
        // satisfy React's child-key requirement (Fragment doesn't
        // forward keys to its children). Use an explicit
        // `<Fragment key=...>` or — as here — drop the wrapper since
        // there's only one conditional child.
        get(permissions, "crewUserStatus.update") ? (
          <CrewUserStatus
            key="crew-user-status"
            id={id}
            status={status}
            onStatusChange={onStatusChange}
            navigationBar
          />
        ) : null,
        <Divider key="divider" />,
        // Page links used to be duplicated into this menu on mobile
        // (isMobileMode ? tabs : []) since there was nowhere else for
        // them to live; the navigation drawer (navItems, above) now
        // covers that, so this menu goes back to being utility-only.

        <MenuItem onClick={clearLastSavedState} key="reset-state">
          <Tooltip title="Click to reset all filters, search terms and current zoom in timeplan">
            <span>Reset application state</span>
          </Tooltip>
        </MenuItem>,
        <MenuItem onClick={clearServiceWorkerCache} key="clear-data">
          <Tooltip
            title={`
              Click to reset app cache.
              Sometimes you need to do it if you don't see changes after new version release
            `}
          >
            <span>Reset cache</span>
          </Tooltip>
        </MenuItem>,
        <MenuItem onClick={onLogout} key="Log out">
          Log out
        </MenuItem>,
      ]}
    />
  );
};

NavigationBar.propTypes = {
  tabs: PropTypes.array.isRequired,
  username: PropTypes.string.isRequired,
  push: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onLogout: PropTypes.func.isRequired,
  avatar: PropTypes.string,
};

export default memo(
  NavigationBar,
  (prev, next) => prev.currentTab !== next.currentTab
);
